import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertCircle, Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import { APP_NAME } from '../lib/appConfig';
import { useLanguage } from '../i18n';
import { handleLocalizedInvalid, clearLocalizedInvalid } from '../lib/localizedValidation';

export const AuthPage = () => {
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect') || '/dashboard';

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Register form
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [regName, setRegName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regCountry, setRegCountry] = useState('SN');
  const [regReferralCode, setRegReferralCode] = useState(
    searchParams.get('referral') || searchParams.get('ref') || searchParams.get('code') || ''
  );

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(loginEmail, loginPassword);
      navigate(redirectUrl);
    } catch (err) {
      setError(err.response?.data?.detail || t('authPage.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await register({
        email: regEmail,
        password: regPassword,
        full_name: regName,
        phone: regPhone || null,
        country: regCountry,
        referral_code: regReferralCode.trim() || undefined,
      });
      if (regReferralCode.trim()) {
        navigate('/tontines?focus=join');
      } else {
        navigate(redirectUrl);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('authPage.registerError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex items-center justify-center p-4">
      <div className="fixed top-4 right-4 z-10">
        <LanguageSelector />
      </div>
      <div className="w-full max-w-md">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 dark:text-stone-400 dark:hover:text-stone-50 mb-8 transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('authPage.backHome')}
        </Link>

        <Card className="card-neo border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-stone-900 to-stone-700 dark:from-stone-100 dark:to-stone-300 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white dark:text-stone-900 font-display font-bold text-xl">T</span>
            </div>
            <CardTitle className="font-display text-2xl">{APP_NAME}</CardTitle>
            <CardDescription>
              {activeTab === 'login' ? t('authPage.loginDescription') : t('authPage.registerDescription')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-3 mb-4 flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">{t('auth.login')}</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">{t('auth.register')}</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form
                  onSubmit={handleLogin}
                  onInvalidCapture={(e) => handleLocalizedInvalid(e, t)}
                  onInputCapture={clearLocalizedInvalid}
                  onChangeCapture={clearLocalizedInvalid}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="login-email">{t('auth.email')}</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder={t('authPage.emailPlaceholder')}
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      className="h-12"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">{t('auth.password')}</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showLoginPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                        className="h-12 pr-12"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword((prev) => !prev)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200 p-1"
                        aria-label={showLoginPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        data-testid="login-password-toggle"
                      >
                        {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {t('auth.loginButton')}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form
                  onSubmit={handleRegister}
                  onInvalidCapture={(e) => handleLocalizedInvalid(e, t)}
                  onInputCapture={clearLocalizedInvalid}
                  onChangeCapture={clearLocalizedInvalid}
                  className="space-y-4"
                >
                  <div className="space-y-2">
                    <Label htmlFor="reg-name">{t('auth.fullName')}</Label>
                    <Input
                      id="reg-name"
                      type="text"
                      placeholder={t('authPage.namePlaceholder')}
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      required
                      className="h-12"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-email">{t('auth.email')}</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder={t('authPage.emailPlaceholder')}
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      required
                      className="h-12"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-phone">{t('authPage.phoneOptional')}</Label>
                    <Input
                      id="reg-phone"
                      type="tel"
                      placeholder={t('authPage.phonePlaceholder')}
                      value={regPhone}
                      onChange={(e) => setRegPhone(e.target.value)}
                      className="h-12"
                      data-testid="register-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-country">{t('auth.country')}</Label>
                    <Select value={regCountry} onValueChange={setRegCountry}>
                      <SelectTrigger className="h-12" data-testid="register-country-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SN">🇸🇳 {t('countries.SN')}</SelectItem>
                        <SelectItem value="CA">🇨🇦 {t('countries.CA')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-password">{t('auth.password')}</Label>
                    <div className="relative">
                      <Input
                        id="reg-password"
                        type={showRegPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        required
                        minLength={6}
                        className="h-12 pr-12"
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowRegPassword((prev) => !prev)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200 p-1"
                        aria-label={showRegPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                        data-testid="register-password-toggle"
                      >
                        {showRegPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reg-referral-code">{t('auth.referralCode')}</Label>
                    <Input
                      id="reg-referral-code"
                      type="text"
                      placeholder="TPK7A7QU"
                      value={regReferralCode}
                      onChange={(e) => setRegReferralCode(e.target.value)}
                      className="h-12"
                      data-testid="register-referral-code-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {t('authPage.createAccountButton')}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
